import type {FC} from 'react';

import {useTheme} from 'styled-components';

import {i18n} from '@yourcoach/i18n';

import type {ThemeColor} from '../../../styles/theme';
import type {Props} from './types';

import {getColor} from '../../../styles/utils';
import {TestID} from '../../../utils/test-utils/data-test-id';
import * as S from './styles';

export const ListItem: FC<Props> = ({
  accentColor,
  ext,
  icon,
  iconVariant = 'square',
  isImage,
  isUrl,
  name,
  onDeleteButtonClick,
  onMoreButtonClick,
  preview,
  size,
  sizeVariant,
  url,
  variant,
  ...rest
}) => {
  const theme = useTheme();
  const Icon = icon;
  const iconFillColor = accentColor
    ? (getColor(accentColor as ThemeColor)({theme}) ?? accentColor)
    : getColor('icon3')({theme});

  const iconStyle = S.getIconStyle(sizeVariant);

  const renderIconButtons = () => (
    <>
      {onDeleteButtonClick ? (
        <S.ClickableIconContainer
          onClick={onDeleteButtonClick}
          testID={TestID.file_list_item.button.delete}
        >
          <S.DeleteIcon />
        </S.ClickableIconContainer>
      ) : null}
      {onMoreButtonClick ? (
        <S.ClickableIconContainer
          onClick={onMoreButtonClick}
          testID={TestID.file_list_item.button.more}
        >
          <S.MoreIcon />
        </S.ClickableIconContainer>
      ) : null}
    </>
  );

  return (
    <S.Container {...rest} testID={TestID.file_list_item.block.container}>
      <S.Attachment
        iconFillColor={iconFillColor}
        iconVariant={iconVariant}
        isImage={isImage}
        placeholder={
          <S.Placeholder>
            <Icon fill={iconFillColor} {...iconStyle} />
          </S.Placeholder>
        }
        sizeVariant={sizeVariant}
        src={preview}
        testID={TestID.file_list_item.block.icon}
        variant={variant}
      />
      <S.Content>
        <S.Name
          accentColor={accentColor}
          sizeVariant={sizeVariant}
          testID={TestID.file_list_item.text.title}
        >
          {name}
        </S.Name>
        {size && !isUrl ? (
          <S.Label
            sizeVariant={sizeVariant}
            testID={TestID.file_list_item.text.size}
          >
            {`${i18n.fileSize(size)}, ${ext.toLowerCase()}`}
          </S.Label>
        ) : null}
        {url ? (
          <S.Label
            sizeVariant={sizeVariant}
            testID={TestID.file_list_item.text.url}
          >
            {url}
          </S.Label>
        ) : null}
      </S.Content>
      {renderIconButtons()}
    </S.Container>
  );
};
